import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getRequest, updateRequestData } from '../api/requests';
import type { RequestDocument } from '../api/requests';

/**
 * Popup that shows on the request detail page when a deviceEnablement
 * request has completed but the user hasn't dismissed the notification yet.
 * Only triggers on /requests/:id pages.
 */
const DeviceCompletionNotification: React.FC = () => {
  const location = useLocation();
  const [pending, setPending] = React.useState<RequestDocument | null>(null);
  const [open, setOpen] = React.useState(false);

  // Extract request ID from URL if on a request detail page
  const requestIdMatch = location.pathname.match(/^\/requests\/([a-f0-9-]+)$/i);
  const requestId = requestIdMatch?.[1] ?? null;

  React.useEffect(() => {
    if (!requestId) {
      setOpen(false);
      setPending(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const request = await getRequest(requestId);
        if (cancelled) return;
        if (
          request.requestType === 'deviceEnablement' &&
          request.status === 'Completed' &&
          request.data?.notificationDismissed !== 'true'
        ) {
          setPending(request);
          setOpen(true);
        } else {
          setOpen(false);
          setPending(null);
        }
      } catch {
        // Silent
      }
    })();

    return () => { cancelled = true; };
  }, [requestId]);

  const handleDismiss = async () => {
    setOpen(false);
    if (pending) {
      try {
        await updateRequestData(pending.id, { notificationDismissed: 'true' });
      } catch {
        // Best-effort dismiss
      }
      setPending(null);
    }
  };

  if (!open || !pending) return null;

  const deviceName = (pending.data?.deviceName as string) || 'your device';

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleIcon sx={{ color: '#107c10', fontSize: 28 }} />
        <span>Device Enabled Successfully</span>
      </DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Your device <strong>{deviceName}</strong> has been successfully re-enabled in Entra ID.
        </Typography>

        <Alert severity="warning" sx={{ my: 2 }}>
          <Typography variant="body2">
            After device is enabled, If your device is <strong>Hybrid-Joined</strong>, It's mandatory to ensure your VPN is connected at least up to <strong>2 hours per week</strong>. This is usually the reason that users are unable to access corp resources from an AADJ device.
          </Typography>
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            For more FAQs, visit{' '}
            <Link href="https://aka.ms/adjblock" target="_blank" rel="noopener">
              aka.ms/adjblock
            </Link>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} variant="contained" color="primary">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceCompletionNotification;
